import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';
import { fetchFcmToken, listenForegroundMessages } from '../lib/firebase';
import notificationService from '../services/notificationService';
import type { MessagePayload } from 'firebase/messaging';

// Service Worker communication helper
const sendNotificationSettingsToServiceWorker = (settings: {
  enabled: boolean;
  sound?: boolean;
}) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'UPDATE_NOTIFICATION_SETTINGS',
      data: settings,
    });
    logger.log('Notification settings sent to service worker:', settings);
  }
};

// Notification sound utility
const playNotificationSound = () => {
  try {
    // Check if user has enabled notification sounds in settings
    const soundEnabled = localStorage.getItem('notificationSoundEnabled');
    if (soundEnabled === 'false') {
      logger.log('Notification sound disabled in settings');
      return;
    }

    // Create audio context for notification sound
    const audioContext = new (window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configure sound (pleasant notification tone)
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz tone
    oscillator.type = 'sine';

    // Volume envelope (fade in/out)
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    // Play sound for 500ms
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);

    logger.log('Notification sound played');
  } catch (error) {
    logger.error('Error playing notification sound:', error);
  }
};

export interface NotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
}

export interface UseNotificationReturn {
  permission: NotificationPermission;
  isSupported: boolean;
  requestPermission: () => Promise<NotificationPermission>;
  showNotification: (options: NotificationOptions) => Promise<Notification | null>;
  showPaymentExpiredNotification: () => Promise<Notification | null>;
  fcmToken: string | null;
  refreshFcmToken: () => Promise<string | null>;
}

export const useNotification = (): UseNotificationReturn => {
  const { t } = useTranslation();

  // Enhanced browser compatibility checks
  const checkNotificationSupport = useCallback(() => {
    if (typeof window === 'undefined') return false;

    // Check for Notification API support
    if (!('Notification' in window)) {
      logger.warn('Browser does not support notifications');
      return false;
    }

    // Check for HTTPS requirement (except localhost)
    if (location.protocol !== 'https:' && !location.hostname.includes('localhost')) {
      logger.warn('Notifications require HTTPS in production');
      return false;
    }

    // Check for service worker support (recommended for reliability)
    if (!('serviceWorker' in navigator)) {
      logger.warn('Service Worker not supported - notifications may be unreliable');
    }

    // Mobile-specific checks
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    if (isMobile) {
      logger.info('Mobile device detected');
    }

    return true;
  }, []);

  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (!checkNotificationSupport()) return 'denied';
    return Notification.permission;
  });
  const [fcmToken, setFcmToken] = useState<string | null>(() => {
    return localStorage.getItem('fcmToken') || null;
  });

  const isSupported = checkNotificationSupport();

  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
      logger.log('Notification permission status:', Notification.permission);
    } else {
      logger.warn('Notifications not supported in this browser');
    }
  }, [isSupported]);

  const refreshFcmToken = useCallback(async (): Promise<string | null> => {
    try {
      const reg =
        'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
      const token = await fetchFcmToken(reg || undefined);
      if (token) {
        setFcmToken(token);
        localStorage.setItem('fcmToken', token);
        // Persist current VAPID key for change detection
        const currentVapidKey = (import.meta.env.VITE_FCM_VAPID_KEY as string) || '';
        if (currentVapidKey) {
          localStorage.setItem('fcmToken', currentVapidKey);
        }
        // Attempt to register token to backend only if endpoint configured
        const registerEndpoint =
          (import.meta.env.VITE_API_V1_NOTIFICATIONS_REGISTER as string) ||
          (import.meta.env.VITE_API_V1_NOTIFICATION_REGISTER as string) ||
          '';
        if (registerEndpoint) {
          void notificationService.registerFcmToken(token);
        }
      }
      return token;
    } catch (error) {
      logger.error('refreshFcmToken error:', error);
      return null;
    }
  }, []);

  // Auto-refresh FCM token for users with already granted permission
  useEffect(() => {
    if (!isSupported) return;
    const currentVapidKey = (import.meta.env.VITE_FCM_VAPID_KEY as string) || '';
    const storedVapidKey = localStorage.getItem('fcmToken') || '';
    const shouldRefresh =
      permission === 'granted' &&
      !!currentVapidKey &&
      (!fcmToken || storedVapidKey !== currentVapidKey);

    if (shouldRefresh) {
      logger.log('Auto refreshing FCM token', {
        hasToken: !!fcmToken,
        vapidChanged: storedVapidKey !== currentVapidKey,
      });
      void refreshFcmToken().then((newToken) => {
        if (newToken) {
          localStorage.setItem('fcmToken', currentVapidKey);
        }
      });
    }
  }, [isSupported, permission, fcmToken, refreshFcmToken]);

  // Register foreground FCM message handler
  // (moved below after showNotification declaration to satisfy linter)

  // Sync notification settings with service worker on mount and permission changes
  useEffect(() => {
    const notificationsEnabled = localStorage.getItem('notificationsEnabled') !== 'false';
    const soundEnabled = localStorage.getItem('notificationSoundEnabled') !== 'false';

    sendNotificationSettingsToServiceWorker({
      enabled: notificationsEnabled && permission === 'granted',
      sound: soundEnabled,
    });
  }, [permission]);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!checkNotificationSupport()) {
      logger.warn('❌ Cannot request permission - notifications not supported');
      return 'denied';
    }

    // Check if permission is already granted
    if (permission === 'granted') {
      logger.info('✅ Notification permission already granted');
      return 'granted';
    }

    // Mobile-specific checks and preparations
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

    if (isMobile) {
      logger.info('📱 Mobile device detected - preparing for permission request');
    }

    try {
      logger.info('🔔 Requesting notification permission...');

      // Use modern Promise-based API if available, fallback to callback
      let result: NotificationPermission;

      if (typeof Notification.requestPermission === 'function') {
        const permissionResult = Notification.requestPermission();

        // Handle both Promise and callback versions
        if (permissionResult && typeof permissionResult.then === 'function') {
          result = await permissionResult;
        } else {
          // Fallback for older browsers
          result = await new Promise<NotificationPermission>((resolve) => {
            Notification.requestPermission((perm) => resolve(perm));
          });
        }
      } else {
        logger.warn('⚠️ Notification.requestPermission not available');
        return 'denied';
      }

      setPermission(result);

      // Provide user feedback based on result
      switch (result) {
        case 'granted':
          logger.info('✅ Notification permission granted successfully');
          // Fetch FCM token after permission is granted
          try {
            const token = await refreshFcmToken();
            if (token) {
              logger.log('FCM token ready');
            } else {
              logger.warn('FCM token not available');
            }
          } catch (err) {
            logger.error('Error fetching FCM token:', err);
          }
          break;
        case 'denied':
          logger.warn('❌ Notification permission denied by user');
          logger.info(
            '💡 To enable notifications, go to browser settings and allow notifications for this site',
          );
          break;
        case 'default':
          logger.warn('⚠️ Notification permission dismissed by user');
          break;
      }

      return result;
    } catch (error) {
      logger.error('❌ Error requesting notification permission:', error);

      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          logger.info('💡 Notification permission blocked by browser policy');
        } else if (error.name === 'AbortError') {
          logger.info('💡 Permission request was aborted');
        }
      }

      setPermission('denied');
      return 'denied';
    }
  }, [checkNotificationSupport, permission, refreshFcmToken]);

  const showNotification = useCallback(
    async (options: NotificationOptions): Promise<Notification | null> => {
      logger.info('🔔 showNotification called:', { options, permission });

      // Enhanced compatibility checks
      if (!checkNotificationSupport()) {
        logger.warn('❌ Notifications not supported on this browser/environment');
        return null;
      }

      if (permission === 'denied') {
        logger.warn('❌ Notification permission denied');
        logger.info('💡 User has denied notifications. Please enable in browser settings.');
        return null;
      }

      if (permission === 'default') {
        logger.info('💡 Requesting notification permission...');
        const newPermission = await requestPermission();
        if (newPermission !== 'granted') {
          logger.warn('❌ Notification permission not granted:', newPermission);
          return null;
        }
      }

      try {
        logger.info('✅ Creating notification...');

        // Cross-browser compatible notification options
        const notificationOptions = {
          body: options.body || '',
          icon: options.icon
            ? `${window.location.origin}${options.icon}`
            : `${window.location.origin}/icon-192x192.svg`,
          badge: `${window.location.origin}/icon-192x192.svg`,
          tag: options.tag || `ez-topup-${Date.now()}`, // Unique tag to prevent grouping issues
          requireInteraction: options.requireInteraction || false,
          silent: options.silent || false,
          timestamp: Date.now(),
        };

        // Remove undefined properties for better browser compatibility
        Object.keys(notificationOptions).forEach((key) => {
          if (notificationOptions[key as keyof typeof notificationOptions] === undefined) {
            delete notificationOptions[key as keyof typeof notificationOptions];
          }
        });

        logger.info('📋 Notification options:', notificationOptions);

        const notification = new Notification(options.title, notificationOptions);

        // Enhanced event listeners with error handling
        notification.onshow = () => {
          logger.info('✅ Notification shown successfully');
        };

        notification.onclick = () => {
          logger.info('👆 Notification clicked');
          try {
            window.focus();
            notification.close();
          } catch (e) {
            logger.warn('Failed to focus window or close notification:', e);
          }
        };

        notification.onclose = () => {
          logger.info('❌ Notification closed');
        };

        notification.onerror = (error) => {
          logger.error('❌ Notification error:', error);
        };

        // Auto-close after 8 seconds for better UX (some browsers don't auto-close)
        setTimeout(() => {
          try {
            notification.close();
            logger.info('🕐 Notification auto-closed after 8 seconds');
          } catch (e) {
            logger.warn('Failed to auto-close notification:', e);
          }
        }, 8000);

        // Play notification sound if not silent
        if (!options.silent) {
          try {
            playNotificationSound();
          } catch (e) {
            logger.warn('Failed to play notification sound:', e);
          }
        }

        // Update service worker with current settings
        sendNotificationSettingsToServiceWorker({
          enabled: true,
          sound: localStorage.getItem('notificationSoundEnabled') !== 'false',
        });

        logger.info('🔔 Notification created successfully:', options.title);
        return notification;
      } catch (error) {
        logger.error('❌ Failed to create notification:', error);
        if (error instanceof Error) {
          logger.error('Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack,
          });

          // Provide specific error guidance
          if (error.name === 'NotAllowedError') {
            logger.info('💡 Notification blocked by browser. Check browser notification settings.');
          } else if (error.name === 'AbortError') {
            logger.info('💡 Notification was aborted. This may happen on mobile browsers.');
          }
        }
        return null;
      }
    },
    [checkNotificationSupport, permission, requestPermission],
  );

  // Register foreground FCM message handler (after showNotification is declared)
  useEffect(() => {
    listenForegroundMessages(async (payload: MessagePayload) => {
      try {
        const notification = payload?.notification as { title?: string; body?: string } | undefined;
        const data = (payload?.data || {}) as Record<string, string>;
        const title = notification?.title || 'My EzTopup';
        const body = notification?.body || data?.body || 'You have a new update';
        const tag = data?.tag || 'fcm';
        await showNotification({ title, body, icon: '/icon-192x192.svg', tag });
      } catch (err) {
        logger.error('Failed to show foreground FCM notification:', err);
      }
    });
  }, [showNotification]);

  // Listen for service worker messages (e.g., pushsubscriptionchange)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleMessage = (event: MessageEvent) => {
      try {
        const type = (event.data && (event.data as { type?: string }).type) || '';
        if (type === 'FCM_SUBSCRIPTION_CHANGED') {
          logger.log('SW: push subscription changed; refreshing FCM token');
          void refreshFcmToken();
        }
      } catch (err) {
        logger.warn('Failed to process SW message', err);
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [refreshFcmToken]);

  // Refresh token when service worker controller changes
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleControllerChange = () => {
      if (permission === 'granted') {
        logger.log('SW controller changed; refreshing FCM token');
        void refreshFcmToken();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [permission, refreshFcmToken]);

  const showPaymentExpiredNotification = useCallback(async (): Promise<Notification | null> => {
    // Check if notifications are enabled in user settings
    const notificationsEnabled = localStorage.getItem('notificationsEnabled');
    if (notificationsEnabled === 'false') {
      logger.log('Notifications disabled in user settings, skipping payment expired notification');
      return null;
    }

    return showNotification({
      title: t('qr_code_expired', 'QR Code Expired'),
      body: t('payment_time_expired', 'Payment time has expired. Please try again.'),
      icon: '/icon-192x192.svg',
      tag: 'payment-expired',
      requireInteraction: true,
      silent: false,
    });
  }, [showNotification, t]);

  return {
    permission,
    isSupported,
    requestPermission,
    showNotification,
    showPaymentExpiredNotification,
    fcmToken,
    refreshFcmToken,
  };
};
